import { Box, Chip, Typography } from '@mui/material';
import { SeeClientsEnum } from '@uplift/types';
import { RadioControl, RadioControlProps, YesNoRadioButtonGroup } from '@uplift/ui-v2';
import { useProfileContext } from '../profile.context';
import { useProfileStyles } from '../styles';
import { FieldArray, FieldArrayRenderProps, useFormikContext } from 'formik';
import { ProfileForm } from './types';
import { useCallback, useEffect } from 'react';
import { useIsMobile, useL10n } from '@uplift/hooks';
import { seeClientsTypeLabel } from '../profile.utils';
import clsx from 'clsx';

export const SessionOptionsForm: React.VFC = () => {
  const { classes } = useProfileStyles();
  const formik = useFormikContext<ProfileForm['sessionOption']>();

  return (
    <Box className={classes.formGroup}>
      <FieldArray
        name="locations"
        render={(arrayHelpers) => formik.values.locations.map((location, index) => (
          <SessionOptionInput
            key={location.locationId}
            arrayHelpers={arrayHelpers}
            index={index}
            location={location}
          />
        ))}
      />
    </Box>
  );
};

type SessionOptionInputProps = {
  index: number;
  arrayHelpers: FieldArrayRenderProps;
  location: ProfileForm['sessionOption']['locations'][number];
};

const inPersonOptions = Array.of<SeeClientsEnum>(SeeClientsEnum.inPerson, SeeClientsEnum.videoAndInPerson);

export const getIsInPersonOption = (seeClientsType: SeeClientsEnum) => inPersonOptions.includes(seeClientsType);

const SessionOptionInput: React.VFC<SessionOptionInputProps> = props => {
  const { classes } = useProfileStyles();
  const l10n = useL10n();
  const seeClientOptions = useSeeClientsOptions(props.location.seeClientsType);
  const { profile } = useProfileContext();
  const formik = useFormikContext<ProfileForm['sessionOption']>();
  const createName = useCallback((name: keyof SessionOptionInputProps['location']) => `${props.arrayHelpers.name}[${props.index}].${name}`, [ props.arrayHelpers.name, props.index ]);

  const locationName = profile.locations.find(location => location.id === props.location.locationId)?.address?.line1 || 'your practice';

  const showHandicapInput = getIsInPersonOption(props.location.seeClientsType);
  const { setFieldValue, setFieldTouched } = formik;

  useEffect(() => {
    if (!showHandicapInput) {
      setFieldValue(createName('handicapAccessible'), null);
      setFieldTouched(createName('handicapAccessible'), null);
    }
  }, [ createName, setFieldTouched, setFieldValue, showHandicapInput ]);

  return (
    <Box className={classes.formGroup}>
      <RadioControl
        label={l10n.providerSettings.sessionOption(locationName)}
        name={createName('seeClientsType')}
        values={seeClientOptions}
      />
      {showHandicapInput && (
        <Box className={classes.greySubsection}>
          <YesNoRadioButtonGroup
            label="Is this address handicap accessible?"
            name={createName('handicapAccessible')}
            asBoolean
          />
        </Box>
      )}
    </Box>
  );
};

const useSeeClientsOptions = (value: SeeClientsEnum): RadioControlProps['values'] => {
  const isMobile = useIsMobile('md');

  return [
    {
      label: seeClientsTypeLabel[SeeClientsEnum.telehealth],
      value: SeeClientsEnum.telehealth,
    },
    {
      label: <InPersonLabel label={seeClientsTypeLabel[SeeClientsEnum.inPerson]} showChip={value === SeeClientsEnum.inPerson && !isMobile} />,
      value: SeeClientsEnum.inPerson,
      contentBelowLabel: value === SeeClientsEnum.inPerson && isMobile ? <InPersonSupportChip /> : null,
    },
    {
      label: <InPersonLabel label={seeClientsTypeLabel[SeeClientsEnum.videoAndInPerson]} />,
      value: SeeClientsEnum.videoAndInPerson,
    },
  ];
};

type InPersonLabelProps = {
  label: string;
  showChip?: boolean;
};

const InPersonLabel: React.VFC<InPersonLabelProps> = props => {

  return (
    <Box display="flex" alignItems="center" columnGap="1rem" whiteSpace="nowrap">
      <Typography>{props.label}</Typography>
      {props.showChip && <InPersonSupportChip />}
    </Box>
  );
};

export type InPersonSupportChipProps = {
  className?: string;
};

export const InPersonSupportChip: React.VFC<InPersonSupportChipProps> = props => {
  const { classes } = useProfileStyles();

  return (
    <Chip
      label="In-person support coming soon"
      variant="outlined"
      size="small"
      className={clsx(classes.inPersonSupportChip, props.className)}
    />
  );
};
